import { useLocalStorage } from '@mantine/hooks';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useDashboard } from './dashboard';
import { useMe, useTeam, useTeamIdContext } from './team';
import {
  isOwnerPreviewTeamSwitchPending,
  resolveOwnerPortalContactId,
} from './owner.utils';

type C = {
  contactId: string | null;
  setContactId: (value: string | null) => void;
};

const ownerPortalContext = createContext<C>({
  contactId: null,
  setContactId: () => {},
});

export function useOwnerPortalContactIdLocalStorage() {
  const { id: meId } = useMe();

  const [contactId, setContactId] = useLocalStorage<string | null>({
    key: `${meId}_owp_cid`, // owp_cid = owner portal contact id
    defaultValue: null,
    getInitialValueInEffect: true,
  });

  return { contactId, setContactId };
}

export const OwnerPortalContactContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { id: meId, ownerAccess = [] } = useMe();
  const [{ id: hydratedTeamId }] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const teamScopeId = selectedTeamId ?? hydratedTeamId;

  const { contactId, setContactId } = useOwnerPortalContactIdLocalStorage();
  const [cti, setCti] = useQueryState('cti', parseAsString);
  const [sti] = useQueryState('sti', parseAsString);
  const isPreviewTeamSwitchPending = isOwnerPreviewTeamSwitchPending({
    queryTeamId: sti,
    activeTeamId: teamScopeId,
  });

  const resolvedContactId = useMemo(
    () =>
      resolveOwnerPortalContactId({
        ownerAccess,
        teamId: teamScopeId,
        contactId,
        queryContactId: cti,
      }),
    [ownerAccess, teamScopeId, contactId, cti]
  );

  useEffect(() => {
    // Wait for user+owner access hydration before consuming cti.
    if (isPreviewTeamSwitchPending) return;
    if (!meId || !teamScopeId || !cti || !ownerAccess.length) return;

    if (resolvedContactId === cti && contactId !== cti) {
      setContactId(cti);
    }

    setCti(null);
  }, [
    meId,
    teamScopeId,
    cti,
    ownerAccess.length,
    isPreviewTeamSwitchPending,
    resolvedContactId,
    contactId,
    setContactId,
    setCti,
  ]);

  const ownerPortalValue = useMemo<C>(
    () => ({
      contactId: resolvedContactId,
      setContactId,
    }),
    [resolvedContactId, setContactId]
  );

  return (
    <ownerPortalContext.Provider value={ownerPortalValue}>
      {children}
    </ownerPortalContext.Provider>
  );
};

export const useOwnerPortalContact = () => {
  const context = useContext(ownerPortalContext);

  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  return useMemo<typeof context>(() => {
    return {
      ...context,
      contactId: isOwnerPortal ? context.contactId : null,
    };
  }, [context, isOwnerPortal]);
};
